import { createSlice } from "@reduxjs/toolkit";
import {
  fetchQuotationVoucherNumber,
  fetchSalesAccounts,
  createQuotation,
} from "./quotationThunk";
import { currentDateTime } from "../../lib/functions";
import Big from "big.js";

// Discount for a single item row: reads whichever mode is active
// (percent, the default/undefined-safe mode, or a flat amount), clamped
// so a flat amount can never exceed the item's own line total.
const getItemDiscountAmount = (item, line) => {
  if (item?.discountMode === "amount") {
    const raw = Big(item?.discountAmt || 0);
    return raw.gt(line) ? line : raw.lt(0) ? Big(0) : raw;
  }
  return line.times(item?.discount || 0).div(100);
};

const recomputeTotals = (state) => {
  let subtotal = Big(0);
  let totalDiscount = Big(0);
  let totalTax = Big(0);

  state.itemCart.forEach((it) => {
    const qty = Big(it.qty || it.item_qty || 0);
    const rate = Big(it.rate || it.item_rate || 0);
    if (it?.bonus) return;

    let lineBase = qty.times(rate);

    if (state.taxMode === "on_item") {
      const discountAmt = getItemDiscountAmount(it, lineBase);
      const afterDiscount = lineBase.minus(discountAmt);

      const taxPer = Big(it.tax_per || 0);
      const taxAmt = afterDiscount.times(taxPer).div(100);

      subtotal = subtotal.plus(afterDiscount.plus(taxAmt));
      totalDiscount = totalDiscount.plus(discountAmt);
      totalTax = totalTax.plus(taxAmt);
    } else {
      subtotal = subtotal.plus(lineBase);
    }
  });

  const serviceCharge = Big(state.footerValues.serviceCharge || 0);
  const serviceCartTotal = (state.servicePayCart || []).reduce(
    (sum, p) => sum.plus(p.tran_amount || 0),
    Big(0)
  );

  const grandTotal = subtotal.plus(serviceCharge).plus(serviceCartTotal);

  state.footerValues.subtotal = subtotal.toFixed(2);
  state.footerValues.total_discount = totalDiscount.toFixed(2);
  state.footerValues.totalTax = totalTax.toFixed(2);
  state.footerValues.grandTotal = grandTotal.toFixed(2);
};



const initialState = {
  voucherNumber: "",
  sale_o_id: null,
  entryDate: currentDateTime(),
  customer: null,
  itemCart: [],
  salesAccounts: [],
  selectedSalesAcc: null,
  servicePayCart: [],
  taxMode: "on_item",
  footerValues: {
    subtotal: "0.00",
    totalTax: "0.00",
    grandTotal: "0.00",
    total_discount: "0.00",
    description: "",
    paidAmount: 0,
    dueAmount: 0,
    serviceCharge: 0,
    discountPct: 0,
    taxPct: 0,
    termsConditions: "",
    paymentTerms: "",
    showTermsConditions: true,
  },
  tagFieldId: null,
  selectedDiscountAcc: null,
  selectedTaxAcc: null,
  conversionRate: 0,
  conversionCurrency: null,
  loading: false,
  error: null,
};

const quotationSlice = createSlice({
  name: "quotation",
  initialState,
  reducers: {
    resetQuotation: () => initialState,
    emptyItemCart: (state) => {state.itemCart = []},
    setVoucherNumber(state, { payload }) {
      state.voucherNumber = payload;
    },
    addItemRow: (state, { payload }) => {
      state.itemCart = [...state.itemCart, payload];
        recomputeTotals(state);
    },
    updateItemRow: (state, { payload }) => {
      const { key, updates } = payload;
      const index = state.itemCart.findIndex((r) => r.key === key);
      if (index !== -1) {
        state.itemCart[index] = { ...state.itemCart[index], ...updates };
      }
      recomputeTotals(state);
    },
    deleteItemRow: (state, { payload }) => {
      state.itemCart = state.itemCart.filter((r) => r.key !== payload);
      recomputeTotals(state);
    },
    setCustomer: (state, { payload }) => {
      state.customer = payload;
    },
    setFooterValues: (state, { payload }) => {
      state.footerValues = { ...state.footerValues, ...payload };
      recomputeTotals(state);
    },
    setTaxMode(state, action) {
      state.taxMode = action.payload;
    },loadQuotationForEdit(state, action) {
  const q = action.payload.quotation;
  const tags = action.payload.tags || [];

  state.sale_o_id = q.sale_o_id;
  state.tagFieldId = tags.map(t => t.id);
  state.voucherNumber = q.sale_order_no;
  state.entryDate = q.created_date;
  state.taxMode = q.is_cal_type === "individual" ? "on_item" : "on_total";

  state.customer = {
    acc_id: q.acc_id,
    acc_name: q.acc_name,
    address: q.address,
    contact_no: q.contact_no,
    institution_name: q.institution_name,
    party_type: q.party_type || "",
    acc_code: q.acc_code,
  };

  state.itemCart = (q.details || []).map((d, index) => ({
    key: index + 1,
    item_id: d.item_id,
    product: d.item_name,
    product_display: d.display_text || d.item_name,
    item_description: d.item_description,
    showDescription: !!d.item_description.length,
    qty: parseFloat(d.item_qty) || 0,
    rate: parseFloat(d.item_rate) || 0,
    discount: parseFloat(d.item_discount_per) || 0,
    discountAmt: parseFloat(d.item_discount) || 0,
    discountMode: d.discount_mode || "percent",
    tax_per: parseFloat(d.item_tax_per) || 0,
    // ✅ same pattern as salesOrderSlice
    bonus: parseFloat(d.item_rate) === 0 ||
           d.bounus_update_check === true ||
           d.bounus_update_check === 1 ||
           d.is_bonus === "yes",
    unit_id: d.unit_id || 0,
    unit_name: d.unit_name || "",
    unit_symbol: d.unit_symbol || "",
    per_unit_id: d.per_unit_id || 0,
    per_unit_symbol: d.per_unit_symbol || "",
    per_conversion: parseFloat(d.per_conversion) || 1,
    conversion: parseFloat(d.conversion) || 1,
    warehouse_id: d.warehouse_id || 0,
    // ✅ store rate variants so useEffect can switch correctly
    sale_rate: d.sale_rate,
    moh_invoice_rate: d.moh_invoice_rate,
    wholesaler_rate: d.wholesaler_rate,
    retailer_rate: d.retailer_rate,
    distributor_rate: d.distributor_rate,
    corporate_rate: d.corporate_rate,
    isBlank: false,
  }));

  const serviceCart = (q.otherServiceTrans || []).map((t, idx) => ({
    key: `expense_${idx}`,
    to_acc_id: t.to_acc_id,
    to_acc_name: t.to_acc_name,
    tran_amount: parseFloat(t.tran_amount) || 0,
    acc_type_id: t.acc_type_id || "direct_income",
  }));

  state.servicePayCart = serviceCart;
  state.footerValues = {
    ...state.footerValues,
    subtotal: q.sub_total,
    total_discount: q.total_discount,
    totalTax: q.total_tax,
    grandTotal: q.total_amount,
    description: q.narration || "",
    payServiceCart: serviceCart,
    addExpenses: serviceCart.length > 0,
    termsConditions: q?.terms_conditions || "",
    paymentTerms: q?.payment_terms || "",
    showTermsAndConditions: q?.show_terms_conditions ,
  };
},
    setServicePayCart: (state, { payload }) => {
      const cart = Array.isArray(payload) ? payload : [];

      state.servicePayCart = cart;

      // 🔥 keep footerValues synced (CRITICAL FIX)
      state.footerValues = {
        ...state.footerValues,
        payServiceCart: cart,
      };
      recomputeTotals(state);
    },

    setDiscountAcc(state, action) {
      state.selectedDiscountAcc = action.payload;
    },
    setTaxAcc(state, action) {
      state.selectedTaxAcc = action.payload;
    },
    setQuotationTagFieldId(state, action) {
      state.tagFieldId = action.payload || null;
    },
    setConversion(state, action) {
      state.conversionCurrency = action.payload || null;
    },
    setConversionRate(state, action) {
      state.conversionRate = action.payload;
    },

    setSaleOrderId(state, action) {
      state.sale_o_id = action.payload;
    },

    calculateTotals(state, action) {
      const authInfo = action?.payload?.authInfo || {};
      let subtotal = Big(0); // base total before discount & tax
      let totalDiscount = Big(0); // accumulated discount
      let totalTax = Big(0); // accumulated tax
      let grandTotal = Big(0); // accumulated final per-line totals

      state.itemCart.forEach((it) => {
        const qty = Big(it.qty || it.item_qty || 0);
        const rate = Big(it.rate || it.item_rate || 0);
        if (it?.bonus) return;

        let lineBase = qty.times(rate);

        if (state.taxMode === "on_item") {
          const discountAmt = getItemDiscountAmount(it, lineBase);
          const afterDiscount = lineBase.minus(discountAmt);
          lineBase = lineBase.minus(discountAmt);

          const taxPer = Big(it.tax_per || it.item_tax_per || 0);
          const taxAmt = afterDiscount.times(taxPer).div(100);
          const lineTotal = afterDiscount.plus(taxAmt);
          lineBase = lineBase.plus(taxAmt);

          subtotal = subtotal.plus(lineBase);
          totalDiscount = totalDiscount.plus(discountAmt);
          totalTax = totalTax.plus(taxAmt);
          grandTotal = grandTotal.plus(lineTotal); // ✅ accumulate final per-item total
        } else {
          subtotal = subtotal.plus(lineBase);
        }
      });

      if (state.taxMode === "on_total") {
        const discountPct = Big(state.footerValues.discountPct || 0);
        totalDiscount = subtotal.times(discountPct).div(100);

        const afterDiscount = subtotal.minus(totalDiscount);

        const taxPct = Big(authInfo?.item_tax || 0);
        totalTax = afterDiscount.times(taxPct).div(100);

        grandTotal = afterDiscount.plus(totalTax); // ✅ global calc in on_total
        state.footerValues.taxPct = taxPct.toString();
      }

      const serviceCharge = Big(state.footerValues.serviceCharge|| 0);
      const serviceCartTotal =
        state.servicePayCart?.reduce(
          (sum, p) => sum.plus(Big(p.tran_amount || 0)),
          Big(0)
        ) || Big(0);

      grandTotal = grandTotal.plus(serviceCharge).plus(serviceCartTotal);

      state.footerValues = {
        ...state.footerValues,
        subtotal: subtotal.toFixed(13),
        total_discount: totalDiscount.toFixed(13),
        totalTax: totalTax.toFixed(13),
        grandTotal: grandTotal.toFixed(13),
        dueAmount: (
          grandTotal.minus(Big(state.footerValues.paidAmount || 0))
        ).toFixed(13),
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchQuotationVoucherNumber.fulfilled, (state, { payload }) => {
        state.voucherNumber = payload;
      })
      .addCase(fetchSalesAccounts.fulfilled, (state, { payload }) => {
        state.salesAccounts = payload;
        if (payload.length > 0) {
          state.selectedSalesAcc = payload[0];
        }
      })
      .addCase(createQuotation.pending, (state) => {
        state.loading = true;
      })
      .addCase(createQuotation.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createQuotation.rejected, (state, { payload }) => {
        state.loading = false;
        state.error = payload;
      });
  },
});

export const {
  resetQuotation,
  emptyItemCart,
  setVoucherNumber,
  addItemRow,
  updateItemRow,
  deleteItemRow,
  setCustomer,
  setFooterValues,
  setTaxMode,
  setDiscountAcc,
  setTaxAcc,
  setServicePayCart,
  setQuotationTagFieldId,
  calculateTotals,
  setConversion,
  setConversionRate,
  setSaleOrderId,
    loadQuotationForEdit
} = quotationSlice.actions;

export default quotationSlice.reducer;
